import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';

// Only pull in createPortal on web to avoid a server-side reference to `document`
const createPortalFn: ((node: React.ReactNode, container: Element) => React.ReactPortal) | null =
  Platform.OS === 'web' ? require('react-dom').createPortal : null;

export interface KpiTargetTooltipProps {
  children: React.ReactNode;
  metricName: string;
  actualValue: string | number;
  targetValue?: string | number | null;
  varianceActualValue?: number | null;
}

// ─── Variance helpers ────────────────────────────────────────────────────────

/** Strip formatting chars and parse a KPI display string to a number. */
const parseKpiNum = (val: string | number): number | null => {
  if (typeof val === 'number') return isNaN(val) ? null : val;
  const n = parseFloat(String(val).replace(/[%,$\s,]/g, ''));
  return isNaN(n) ? null : n;
};

/**
 * Return a signed variance string (e.g. "+2%", "-82") if both values are
 * numeric, otherwise null.
 * - In House Cut (IHC) and Gap Time: 2 decimal places
 * - Injury/RIR: whole number (0 decimal places)
 * - All others: 1 decimal place
 */
const calcVariance = (actual: string | number | null, target: string | number | null, metricName: string): string | null => {
  if (actual == null || target == null) return null;
  const a = parseKpiNum(actual);
  const t = parseKpiNum(target);
  if (a === null || t === null) return null;
  const diff = a - t;
  const sign = diff > 0 ? '+' : '';
  const hasPct = String(actual).includes('%') || String(target).includes('%');
  const upperName = metricName.toUpperCase();
  let dp: number;
  if (upperName.includes('IHC') || upperName.includes('IN HOUSE CUT') || upperName.startsWith('GAP TIME')) {
    dp = 2;
  } else if (upperName === 'RIR' || upperName.includes('INJURY')) {
    dp = 0;
  } else {
    dp = 1;
  }
  const formatted = diff.toFixed(dp);
  return `${sign}${formatted}${hasPct ? '%' : ''}`;
};

// ─── Portal container (shared with DLOTooltip if already present) ────────────

const PORTAL_ID = 'dlo-tooltip-container';

function ensurePortal(): HTMLElement {
  let el = document.getElementById(PORTAL_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = PORTAL_ID;
    Object.assign(el.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '999999',
    });
    document.body.appendChild(el);
  }
  return el;
}

// ─── Web-only inner component ────────────────────────────────────────────────

/** Fixed tooltip width — keeps layout stable and avoids reflow on each hover. */
const TW = 180;
const GAP = 12;
const ARROW = 5; // half-size of the rotated-square arrow
/**
 * Conservative tooltip height for placement decisions and top-offset math.
 * Actual rendered height with 3 rows ≈ 98px; use 110 to ensure the tooltip
 * bottom never lands inside the hovered cell.
 */
const TOOLTIP_H = 110;

interface TPos {
  top: number;
  left: number;
  /** px from tooltip left edge to arrow centre — already clamped */
  arrowLeft: number;
  placement: 'top' | 'bottom';
}

/** Rendered only on web and only when targetValue is non-null. */
const KpiTooltipWeb: React.FC<{
  children: React.ReactNode;
  metricName: string;
  actualValue: string | number;
  targetValue: string | number;
  varianceActualValue?: number | null;
}> = ({ children, metricName, actualValue, targetValue, varianceActualValue }) => {
  const wrapRef = useRef<View>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portalRef = useRef<HTMLElement | null>(null);
  const isHoveringRef = useRef(false);
  const shownAtRef = useRef<number | null>(null);
  const [pos, setPos] = useState<TPos | null>(null);

  const variance = calcVariance(varianceActualValue === undefined ? actualValue : varianceActualValue, targetValue, metricName);

  // Minimum display duration to prevent flickering during rapid hover transitions
  const MIN_DISPLAY_DURATION = 800;  // Increased from 400ms to eliminate flicker from rapid hover events

  useEffect(() => {
    portalRef.current = ensurePortal();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const computePos = useCallback(() => {
    const node = wrapRef.current as any;
    // React Native Web exposes the DOM node via _nativeTag; fall back to the
    // ref itself when it already IS a DOM element (e.g. in tests).
    const dom: HTMLElement | null =
      node?.nodeType === 1 ? node : (node?._nativeTag ?? null);
    if (!dom?.getBoundingClientRect) return;

    const rect = dom.getBoundingClientRect();
    // Use the cell-centre as the vertical anchor so the arrow points to the
    // middle of the value, and the tooltip clears the full row in both directions.
    const cellCenterY = rect.top + rect.height / 2;
    
    // Improved placement logic: check both top and bottom space against viewport boundaries
    const topSpaceAvailable = cellCenterY - TOOLTIP_H - GAP >= 8;
    const bottomSpaceAvailable = cellCenterY + GAP + TOOLTIP_H <= window.innerHeight - 8;
    
    // Prefer top if available, fall back to bottom, or top as last resort
    const placement: TPos['placement'] = topSpaceAvailable ? 'top' : 'bottom';
    const top =
      placement === 'top'
        ? Math.max(8, cellCenterY - TOOLTIP_H - GAP)   // tooltip sits above cell centre, but not above viewport
        : Math.min(cellCenterY + GAP, window.innerHeight - TOOLTIP_H - 8);  // tooltip sits below cell centre, but not below viewport

    const cx = rect.left + rect.width / 2;
    let left = cx - TW / 2;
    if (left < 8) left = 8;
    // Ensure tooltip doesn't overflow the right edge of the viewport
    const rightEdge = window.innerWidth - 16;
    if (left + TW > rightEdge) left = rightEdge - TW;

    setPos({
      top,
      left,
      arrowLeft: Math.max(8, Math.min(cx - left - ARROW, TW - 16)),
      placement,
    });
  }, []);

  const show = useCallback(() => {
    isHoveringRef.current = true;
    
    // Clear any pending computation
    if (timerRef.current) clearTimeout(timerRef.current);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    
    // Debounced computation: wait 200ms and only compute if still hovering
    debounceTimerRef.current = setTimeout(() => {
      if (isHoveringRef.current) {
        // Record when tooltip becomes visible
        shownAtRef.current = Date.now();
        computePos();
      }
    }, 200);
  }, [computePos]);

  const hide = useCallback(() => {
    isHoveringRef.current = false;
    
    // Check if tooltip has been visible for minimum duration
    if (shownAtRef.current !== null) {
      const visibleDuration = Date.now() - shownAtRef.current;
      if (visibleDuration < MIN_DISPLAY_DURATION) {
        // Too soon to hide - re-show instead to prevent flickering
        timerRef.current = setTimeout(() => {
          if (!isHoveringRef.current) {
            // Only hide if still not hovering after grace period
            shownAtRef.current = null;
            setPos(null);
          }
        }, MIN_DISPLAY_DURATION - visibleDuration);
        return;
      }
    }
    
    // Clear any pending show timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Hide and reset tracking
    shownAtRef.current = null;
    setPos(null);
  }, [MIN_DISPLAY_DURATION]);

  const tooltip = pos ? (
    <div
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        pointerEvents: 'none',   // never captures hover — no oscillation
        width: TW,
        backgroundColor: '#1E2127',
        borderRadius: 5,
        padding: '7px 10px 8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.30)',
        fontFamily: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
        fontSize: 12,
        lineHeight: '17px',
        color: '#FFFFFF',
        userSelect: 'none',
        overflow: 'visible', // allow arrow to protrude
      }}
    >
      {/* Title */}
      <div style={{
        fontWeight: 600,
        fontSize: 12,
        marginBottom: 5,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {metricName}
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginBottom: 5 }} />

      {/* Actual */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
        <span style={{ color: '#9CA3AF' }}>Actual</span>
        <span style={{ fontWeight: 500 }}>{String(actualValue)}</span>
      </div>

      {/* Target */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: variance !== null ? 2 : 0,
      }}>
        <span style={{ color: '#9CA3AF' }}>Target</span>
        <span style={{ fontWeight: 500 }}>{String(targetValue)}</span>
      </div>

      {/* Delta (optional third line) */}
      {variance !== null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: '#9CA3AF' }}>Delta</span>
          <span style={{ fontWeight: 600, color: '#E5E7EB' }}>{variance}</span>
        </div>
      )}

      {/* Arrow (rotated square — protrudes outside the box) */}
      <div style={{
        position: 'absolute',
        [pos.placement === 'top' ? 'bottom' : 'top']: -ARROW,
        left: pos.arrowLeft,
        width: ARROW * 2,
        height: ARROW * 2,
        backgroundColor: '#1E2127',
        transform: 'rotate(45deg)',
      }} />
    </div>
  ) : null;

  return (
    <>
      <View
        ref={wrapRef}
        // @ts-ignore — web-only mouse events; invisible to native
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </View>
      {tooltip && portalRef.current && createPortalFn
        ? createPortalFn(tooltip, portalRef.current)
        : null}
    </>
  );
};

// ─── Public component ────────────────────────────────────────────────────────

/**
 * Lightweight KPI hover tooltip for web.
 * - Renders children unchanged on native (zero overhead).
 * - On web, wraps children in a hover trigger and shows a compact
 *   portal tooltip with Actual / Target / Variance.
 * - The tooltip uses `pointerEvents: none` so it never blocks interaction
 *   with neighbouring table cells.
 */
const KpiTargetTooltip: React.FC<KpiTargetTooltipProps> = ({
  children,
  metricName,
  actualValue,
  targetValue,
  varianceActualValue,
}) => {
  if (Platform.OS !== 'web' || targetValue == null || targetValue === '') {
    return <>{children}</>;
  }
  return (
    <KpiTooltipWeb
      metricName={metricName}
      actualValue={actualValue}
      targetValue={targetValue}
      varianceActualValue={varianceActualValue}
    >
      {children}
    </KpiTooltipWeb>
  );
};

export default KpiTargetTooltip;
